import env, {ENV} from 'env';
import {DeviceType} from 'ngx-device-detector';

import {isDevMode} from '@angular/core';

import {EnvironmentName} from '../../types/typings';

export type IEnvironmentStructure = {
	name: EnvironmentName;
	readonly production: boolean;
	DEV_MODE: boolean;

	company: string | undefined;

	apiURL: string;

	deviceType: DeviceType;

	indexedDB?: {
		version: number;
	};

	env: ENV;
};

/**
 * URL structure (in prod):
 * https://{company}.doccycle.{env}/{...params}
 */
export class BaseEnvironment implements IEnvironmentStructure {
	production: boolean;
	name: EnvironmentName | undefined;
	readonly DEV_MODE: boolean = localStorage.getItem('DEV_MODE') === null ? null : localStorage.getItem('DEV_MODE') === 'true';

	company: string;

	deviceType: DeviceType = DeviceType.Unknown;
	direction: 'ltr' | 'rtl' = 'ltr';

	indexedDB: {version: number};

	public readonly env: ENV = env;

	public readonly TIME = {
		SECOND: 1000,
		MINUTE: 60 * 1000,
		HOUR: 60 * 60 * 1000,
		DAY: 24 * 60 * 60 * 1000,
	} as const;

	public readonly AuthSettings = {
		tokenLifetimeMs: 30 * this.TIME.MINUTE,
		clockSkewMs: 5 * this.TIME.MINUTE,
		refreshBufferMs: this.TIME.MINUTE,
	} as const;

	constructor(
		production: boolean,
		name: EnvironmentName | undefined,
		company: string | undefined,
		indexedDB?: {
			version: number;
		},
		apiURL?: string,
		customEnv = false,
	) {
		this.production = production;

		let parsedURL = undefined;
		if (customEnv || !name || !apiURL || window.location.hostname.includes('localhost')) {
			parsedURL = this.getLocationUrlParsed(customEnv);
		}
		this.name = name || parsedURL?.environmentName;
		if (this.DEV_MODE === true && !this.name.endsWith('-local')) this.name += '-local';
		this.company = parsedURL?.company;
		this._apiURL = apiURL || parsedURL?.apiUrl;
		if (!production) this.apiURL = undefined;

		this.indexedDB = indexedDB || {version: 1};
	}

	private _apiURL: string;

	get apiURL(): string {
		return isDevMode() && env.apiUrl ? `${env.apiUrl}/api/` : this._apiURL;
	}

	set apiURL(backendApiUrl: string | undefined) {
		if (backendApiUrl) {
			this._apiURL = `${backendApiUrl}/api/`;
		} else {
			const starsCount = (this.apiURL.match(/\*/g) || []).length;
			if (starsCount === 1) {
				this._apiURL = this._apiURL.replace('*.', `${this.company}.`);
			} else if (starsCount === 2) {
				// the first star in string is replaced with the company name
				// 2nd star in string is replaced with the environment name
				const firstStarIndex = this.apiURL.indexOf('*');
				this._apiURL = this._apiURL.slice(0, firstStarIndex) + this.company + this._apiURL.slice(firstStarIndex + 1);
				const secondStarIndex = this._apiURL.indexOf('*');
				this._apiURL = this._apiURL.slice(0, secondStarIndex) + this.name + this._apiURL.slice(secondStarIndex + 1);
			}
		}
	}

	private getLocationUrlParsed(customEnv: boolean): {
		environmentName: EnvironmentName;
		company: string;
		apiUrl: string;
	} {
		let hostname: string = window.location.hostname;
		const pathname: string = window.location.pathname;

		if (hostname.includes('localhost')) {
			if (customEnv === true || window.location.host.includes(':8080')) {
				// local url can be like:http://{company}.{env}.localhost:4200/{...params} => for debug.dev.localhost:4200/{...params} => [debug, dev,'localhost']
				const hostParts: string[] = hostname.split('.');
				if (hostParts.length === 3) {
					const company = hostParts[0];
					const environmentName = hostParts[1] as EnvironmentName;
					hostname = `${company}.doccycle.${environmentName}`;
					const apiUrl = `https://${company}.doccycle.${environmentName}/api/`;
					return {environmentName, company, apiUrl};
				}
			} else {
				hostname = 'debug.doccycle.dev';
			}
		}

		const splitHost: string[] = hostname.split('.');
		let company: string;
		let environmentName: EnvironmentName;
		// Common Case:
		if (splitHost.length === 3) {
			company = splitHost[0];
			environmentName = splitHost[2] as EnvironmentName;
		}
		// Edge Case (only for urls like: https://doccycle.{env}/sign/{:company}/{:id}/?{:user}):
		else if (splitHost.length === 2) {
			environmentName = splitHost[1] as EnvironmentName;
			const params: string[] = pathname.split('/');
			if (params[1] === 'sign') {
				company = pathname.split('/')[2];
			}
		} else {
			throw new Error('Invalid URL' + window.location.href);
		}

		// TODO: TODO: TODO: TODO: TODO: TODO: TODO: TODO: TODO:
		if (this.production) {
			if (company === 'mono') company = 'debug';
			if ((environmentName as any) === 'tools') environmentName = 'dev';
		}

		const apiUrl = `https://${company}.doccycle.${environmentName}/api/`;

		return {environmentName, company, apiUrl};
	}
}
